/**
 * Iter 259 — Gestion des accès : vue matrice membres × modules.
 *
 * - Tableau : 1 ligne par membre, 1 colonne par module actif, badge x/y permissions.
 * - Clic sur une cellule → panneau inline avec les switches du module (sauvegarde immédiate).
 * - Preset métier applicable par membre + lien vers la fiche détaillée.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import * as LucideIcons from "lucide-react";
import {
  KeyRound, Search, Crown, User, Loader2, ExternalLink, ArrowLeft, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { BUSINESS_LABELS, BUSINESS_COLORS } from "../constants/roleConstants";

export default function TeamAccessPage({ embedded = false }) {
  const { user: currentUser, axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const navigate = useNavigate();

  const [members, setMembers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null); // { userId, module }
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [ov, cat, pre] = await Promise.all([
        api.get("/tenant/members/permissions/overview"),
        api.get("/tenant/permissions/catalog"),
        api.get("/tenant/permissions/presets"),
      ]);
      setMembers(ov.data.items || []);
      setCatalog(cat.data.items || []);
      setPresets(pre.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de charger les accès");
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setMemberPerms = (userId, perms) =>
    setMembers(ms => ms.map(m => (m.user_id === userId ? { ...m, module_permissions: perms } : m)));

  const togglePerm = async (member, permKey) => {
    const next = { ...(member.module_permissions || {}), [permKey]: !member.module_permissions?.[permKey] };
    setMemberPerms(member.user_id, next); // optimiste
    setSavingId(member.user_id + permKey);
    try {
      const r = await api.put(`/tenant/members/${member.user_id}/permissions`, { module_permissions: next });
      setMemberPerms(member.user_id, r.data.module_permissions);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de sauvegarde");
      load();
    }
    setSavingId(null);
  };

  const applyPreset = async (member, presetKey) => {
    const p = presets.find(x => x.key === presetKey);
    if (!p) return;
    if (!window.confirm(`Appliquer le preset "${p.label}" à ${member.name || member.email} ?\n\nToutes ses permissions actuelles seront remplacées.`)) return;
    try {
      const r = await api.post(`/tenant/members/${member.user_id}/permissions/preset`, { preset: presetKey });
      setMemberPerms(member.user_id, r.data.module_permissions);
      toast.success(`Preset "${p.label}" appliqué`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const cellStats = (member, mod) => {
    const perms = member.module_permissions || {};
    const keys = mod.permissions.map(p => p.key);
    return { on: keys.filter(k => perms[k]).length, total: keys.length };
  };

  const filtered = members.filter(m =>
    !search || (m.name || m.email || "").toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;
  }

  return (
    <div className={embedded ? "space-y-5 mt-2" : "p-6 max-w-[1400px] mx-auto space-y-5"} data-testid="team-access-page">
      {!embedded && (
        <Button variant="ghost" size="sm" onClick={() => navigate("/tenant/team")} className="text-slate-400 -mb-2" data-testid="back-to-team-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'équipe
        </Button>
      )}
      <PageHeader
        icon={<KeyRound />}
        title="Gestion des accès"
        description={`${filtered.length} membre(s) × ${catalog.length} module(s) actif(s) — cliquez sur une cellule pour ajuster les permissions`}
        testId="team-access-header"
      />

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input placeholder="Rechercher un membre..." value={search} onChange={e => setSearch(e.target.value)}
          className="pl-10 bg-[#0b0811] border-white/10 text-white text-sm" data-testid="access-search" />
      </div>

      <div className="bg-[#14101c] border border-white/5 rounded-xl overflow-x-auto" data-testid="access-matrix">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="sticky left-0 bg-[#14101c] text-left px-4 py-3 text-[11px] uppercase tracking-wider text-slate-500 min-w-[220px] z-10">Membre</th>
              {catalog.map(mod => {
                const Icon = LucideIcons[mod.icon] || LucideIcons.Folder;
                return (
                  <th key={mod.module} className="px-2 py-3 text-center min-w-[90px]" title={mod.label}>
                    <div className="flex flex-col items-center gap-1 text-slate-400">
                      <Icon className="w-4 h-4 text-[#f7c935]" />
                      <span className="text-[9px] uppercase tracking-wide leading-tight max-w-[80px] truncate">{mod.label}</span>
                    </div>
                  </th>
                );
              })}
              <th className="px-3 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500 min-w-[170px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(m => {
              const isAdmin = m.role === "tenant_admin";
              const isSelf = m.user_id === currentUser?.user_id;
              const RoleIcon = isAdmin ? Crown : User;
              return (
                <MemberRows
                  key={m.user_id}
                  member={m}
                  isAdmin={isAdmin}
                  isSelf={isSelf}
                  RoleIcon={RoleIcon}
                  catalog={catalog}
                  presets={presets}
                  expanded={expanded}
                  setExpanded={setExpanded}
                  cellStats={cellStats}
                  togglePerm={togglePerm}
                  applyPreset={applyPreset}
                  savingId={savingId}
                  navigate={navigate}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        <Crown className="w-3 h-3 inline text-[#f7c935] mr-1" />
        Les administrateurs du tenant ont automatiquement accès à tous les modules. Les permissions marquées « Admin only » ne sont jamais activables pour un membre.
      </p>
    </div>
  );
}

function MemberRows({ member: m, isAdmin, isSelf, RoleIcon, catalog, presets, expanded, setExpanded, cellStats, togglePerm, applyPreset, savingId, navigate }) {
  const isRowExpanded = expanded?.userId === m.user_id;
  const expandedMod = isRowExpanded ? catalog.find(c => c.module === expanded.module) : null;

  return (
    <>
      <tr className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`access-row-${m.user_id}`}>
        <td className="sticky left-0 bg-[#14101c] px-4 py-3 z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#0b0811] flex items-center justify-center text-sm font-bold text-[#f7c935] flex-shrink-0">
              {(m.name || m.email || "?")[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-bold text-white truncate flex items-center gap-1.5">
                {m.name || m.email}
                {isSelf && <span className="text-[9px] text-[#f7c935]">(vous)</span>}
              </div>
              {m.poste && (
                <div className="text-[10px] font-semibold truncate" style={{ color: "#38bdf8" }} data-testid={`access-poste-${m.user_id}`}>
                  {m.poste}
                </div>
              )}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] font-bold uppercase flex items-center gap-1"
                  style={{ color: isAdmin ? "#3b82f6" : "#22c55e" }}>
                  <RoleIcon className="w-3 h-3" /> {isAdmin ? "Admin" : "Membre"}
                </span>
                {(m.roles || []).slice(0, 2).map(rk => (
                  <span key={rk} className="text-[8px] px-1 py-0.5 rounded font-bold"
                    style={{ background: `${BUSINESS_COLORS[rk] || "#666"}20`, color: BUSINESS_COLORS[rk] || "#999" }}>
                    {BUSINESS_LABELS[rk] || rk}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </td>
        {catalog.map(mod => {
          if (isAdmin) {
            return (
              <td key={mod.module} className="px-2 py-3 text-center">
                <span className="inline-flex items-center justify-center px-2 py-1 rounded text-[10px] font-bold bg-[#f7c935]/10 text-[#f7c935]" title="Accès total (admin)">
                  <Crown className="w-3 h-3" />
                </span>
              </td>
            );
          }
          const { on, total } = cellStats(m, mod);
          const color = on === 0 ? "#64748b" : on === total ? "#22c55e" : "#f59e0b";
          const isActive = isRowExpanded && expanded.module === mod.module;
          return (
            <td key={mod.module} className="px-2 py-3 text-center">
              <button
                onClick={() => setExpanded(isActive ? null : { userId: m.user_id, module: mod.module })}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold transition-colors ${isActive ? "ring-1 ring-[#f7c935]" : "hover:brightness-125"}`}
                style={{ background: `${color}18`, color }}
                data-testid={`access-cell-${m.user_id}-${mod.module}`}
              >
                {on}/{total}
                <ChevronDown className={`w-3 h-3 transition-transform ${isActive ? "rotate-180" : ""}`} />
              </button>
            </td>
          );
        })}
        <td className="px-3 py-3 text-right">
          <div className="flex items-center justify-end gap-2">
            {!isAdmin && presets.length > 0 && (
              <select
                value=""
                onChange={e => { if (e.target.value) applyPreset(m, e.target.value); e.target.value = ""; }}
                className="bg-[#0b0811] border border-white/10 rounded px-2 py-1 text-[11px] text-slate-300 max-w-[110px]"
                data-testid={`preset-select-${m.user_id}`}
              >
                <option value="">Preset...</option>
                {presets.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            )}
            <button
              onClick={() => navigate(`/tenant/team/${m.user_id}`)}
              className="p-1.5 rounded hover:bg-white/10 text-cyan-400" title="Fiche détaillée"
              data-testid={`open-profile-${m.user_id}`}
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
      {expandedMod && !isAdmin && (
        <tr className="border-b border-white/5 bg-black/30" data-testid={`access-detail-${m.user_id}-${expandedMod.module}`}>
          <td colSpan={catalog.length + 2} className="px-6 py-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-3">
              {expandedMod.label} — permissions de {m.name || m.email}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {expandedMod.permissions.map(p => {
                const val = !!(m.module_permissions || {})[p.key];
                const disabled = !!p.admin_only;
                const saving = savingId === m.user_id + p.key;
                return (
                  <div key={p.key}
                    className={`flex items-center justify-between gap-3 px-3 py-2 rounded bg-white/[0.03] ${disabled ? "opacity-40" : ""}`}
                    data-testid={`matrix-perm-${m.user_id}-${p.key}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-white truncate">{p.label}</div>
                      {p.admin_only && <span className="text-[9px] text-blue-400 font-bold uppercase">Admin only</span>}
                    </div>
                    {saving
                      ? <Loader2 className="w-4 h-4 animate-spin text-[#f7c935]" />
                      : <Switch checked={val} disabled={disabled} onCheckedChange={() => !disabled && togglePerm(m, p.key)}
                          data-testid={`matrix-switch-${m.user_id}-${p.key}`} />}
                  </div>
                );
              })}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
